import PropTypes from "prop-types";

// @mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";

// components
import Iconify from "src/components/iconify";
import Button from "src/consuman-components/generic/button";
import { useLocales } from "src/locales";

// ----------------------------------------------------------------------

/**
 * Shows what the system heard so the user can correct it before anything touches the form.
 *
 * This is a local dialog rather than the app-wide singleton (`alertDialogRef`) for three reasons: the
 * microphone also renders outside `RefsProvider` (the generic text field is used on the claims
 * registration screen, where `useRefs()` returns null), the singleton snapshots its `child` into state
 * so an editable field could not report its value back, and its agree handler always closes — while
 * this dialog has to stay open with a loading state during interpretation, and stay open when
 * interpretation comes back without a usable value so the user can rephrase.
 */
export default function VoiceTranscriptDialog({
  open,
  value,
  onChange,
  loading,
  onConfirm,
  onCancel,
  onRecordAgain,
}) {
  const { t } = useLocales();

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={loading ? undefined : onCancel}
    >
      <DialogTitle>{t("voiceConfirmTitle")}</DialogTitle>

      <DialogContent>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 2 }}
        >
          {t("voiceConfirmHelper")}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          label={t("voiceTranscript")}
          value={value}
          disabled={loading}
          onChange={event => onChange(event.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onCancel}
          color="error"
          text={t("cancel")}
          disabled={loading}
        />
        <Button
          onClick={onRecordAgain}
          icon="mdi:microphone"
          text={t("voiceRecordAgain")}
          disabled={loading}
        />
        <LoadingButton
          variant="contained"
          size="small"
          loading={loading}
          disabled={!value?.trim()}
          startIcon={<Iconify icon="mdi:send" />}
          onClick={onConfirm}
        >
          {t("voiceApply")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

VoiceTranscriptDialog.propTypes = {
  open: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  onRecordAgain: PropTypes.func,
};
